import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';

import { Store } from '@ngrx/store';

import { Observable, Subscription } from 'rxjs';

import { Study } from '../../../../../models/study';
import { Intervention } from '../../../../../models/intervention';
import { Module } from '../../../../../models/module';
import { AppState } from '../../../../../state';
import { Metrics } from '../../../../../models/metrics';
import { ModuleItem } from '../../../../../models/module-item';
import {
  selectIsLoadingMetrics,
  selectMetrics,
  selectMetricsError,
  selectMetricsLoaded,
} from '../../../../state/metrics/metrics.selectors';
import { getLanguageByCode } from '../../../../../core/languages-helper';
import { Participant } from '../../../../../models/participant';
import { StudyType } from '../../../../../models/study-type';
import { ModuleItemAvailableType } from '../../../../../models/module-item-type';
import { ModuleItemAvailableSubType } from '../../../../../models/module-item-sub-type';
import { MatDialog } from '@angular/material/dialog';
import { MetricsDialogComponent } from '../../../components/metrics-image-dialog/metrics-dialog.component';
import { ModuleItemAvailableAttribute } from '../../../../../models/module-item-attribute';
import { ModuleParticipant } from '../../../../../models/module-participant';
import {
  HEALTH_DATA_SLEEP_VALUES,
  HEALTH_DATA_TYPES,
  HEALTH_DATA_VALUES,
  PHENOTYPING_DATA_TYPES,
  PHENOTYPING_DATA_VALUES,
  LANGUAGES,
  MODULE_TYPE,
} from '../../../../../core/dashboard-constants';
import { changeDateFormat, millisecondsToTime } from '../../../helpers/date-helper';
import { HealthValue } from '../../../../../models/health-value';
import { HealthActivity } from '../../../../../models/health-activity';
import { HealthData } from '../../../../../models/health-data';
import { OrbitItem } from '../../../../../models/orbit-item';
import { Language } from '../../../../../models/language';
import { MetricsComponent } from '../metrics.component';
import { J } from '@angular/cdk/keycodes';
import { ResponseTiming } from '../../../../../models/response-timing';
import { ParticipantIterationPivotId } from '../../../../../models/participant-iteration-pivot-id';
import { PhenotypingValue } from '../../../../../models/phenotyping-value';
import { PhenotypingData } from '../../../../../models/phenotyping-data';

export const METRICS_TIMING_FORMAT = 'MMM d, y, h:mm';
export const HEALTH_DATE_FORMAT = 'MMM d, y, HH:mm:ss';
export const RESPONSES_TYPE = {
  INDEX: 'index',
  TEXT: 'text',
  ORBIT: 'orbit',
  NETWORK: 'network',
  NUMBER: 'number',
  IVY: 'text',
};
export const ANSWERS_TYPE = {
  TEXT: 'text',
  ICON: 'icon',
  LINKS: 'links',
  ITEMS: 'items',
};
export const MODULE_ITEM_QUESTION_LENGTH_LIMIT = 50;
export const INVALID_HEALTH_DATA_TYPE = '{"heart_rate":"c","activity":"o","steps":"m"}';

@Component({
  selector: 'dashboard-metrics-preview',
  templateUrl: './metrics-preview.component.html',
  styleUrls: ['./metrics-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MetricsPreviewComponent implements OnInit, OnDestroy {
  @Input() selectedStudy: Study;
  @Input() showTimings: boolean;
  @Input() selectedLanguage: Language;
  @Input() participants: Participant[];
  @Input() participantIndex: number;

  loadingMetrics$: Observable<boolean>;
  metricsLoaded$: Observable<boolean>;
  error$: Observable<string>;

  metrics: Metrics;
  interventions: Intervention[];
  modules: Module[];

  selectedModuleItem: ModuleItem;

  columnLabels = ['participantId', 'answer'];
  participantIds = [];

  ModuleItemAvailableSubType = ModuleItemAvailableSubType;
  StudyType = StudyType;

  METRICS_TIMING_FORMAT = METRICS_TIMING_FORMAT;
  HEALTH_DATA_TYPES = HEALTH_DATA_TYPES;
  HEALTH_DATA_VALUE = HEALTH_DATA_VALUES;
  HEALTH_DATE_FORMAT = HEALTH_DATE_FORMAT;
  PHENOTYPING_DATA_TYPES = PHENOTYPING_DATA_TYPES;
  PHENOTYPING_DATA_VALUE = PHENOTYPING_DATA_VALUES;
  RESPONSES_TYPE = RESPONSES_TYPE;
  ANSWERS_TYPE = ANSWERS_TYPE;
  MODULE_ITEM_QUESTION_LENGTH_LIMIT = MODULE_ITEM_QUESTION_LENGTH_LIMIT;
  MODULE_TYPE = MODULE_TYPE;

  esmSelectedIteration: { [key: string]: ParticipantIterationPivotId } = {};
  pipList: ParticipantIterationPivotId[] = [];
  selectedPiP: ParticipantIterationPivotId = null;
  esmIterationModuleId: { [key: string]: number } = {};

  changeDateFormat = changeDateFormat;

  private subscriptions = new Subscription();

  constructor(private store: Store<AppState>, private dialog: MatDialog) {}

  ngOnInit(): void {
    this.interventions = this.selectedStudy?.interventions;
    this.modules = this.selectedStudy?.modules;
    this.participantIndex = null;

    this.loadingMetrics$ = this.store.select(selectIsLoadingMetrics);
    this.metricsLoaded$ = this.store.select(selectMetricsLoaded);
    this.error$ = this.store.select(selectMetricsError);

    this.subscriptions.add(
      this.store.select(selectMetrics).subscribe((metrics) => {
        this.metrics = metrics;
        this.participantIds = metrics?.participants?.map((participant) => participant.id.toString());
        if (this.participantIds) {
          this.columnLabels = ['participantId', ...this.participantIds];
        }
      })
    );
  }

  getParticipantByIdFromMetrics(participantId: number): Participant {
    return this.metrics.participants.find((part) => part.id === participantId);
  }

  getParticipantByDeviceIdFromMetrics(deviceId: number): Participant {
    return this.metrics.participants.find((part) => part.studyPivots[0].deviceId === deviceId);
  }

  getModuleResponseTimings(module: Module): ModuleParticipant[] {
    const moduleParticipantTimings = [];
    this.metrics?.participants.map((participant) => {
      const moduleParticipant = participant?.modulePivots.find(
        (responseTiming) => responseTiming.moduleId === module.id
      );
      moduleParticipantTimings.push(moduleParticipant);
    });

    return moduleParticipantTimings;
  }

  getResponsesLanguage(participant: Participant): string {
    return participant?.studyPivots?.length
      ? getLanguageByCode(participant?.studyPivots[0]?.language)?.name
      : LANGUAGES[0].name;
  }

  getModuleName(module: Module): string {
    if (module?.enrolment) {
      return 'Screening questions';
    } else {
      return module.name + this.checkIfModuleIsESM(module.id);
    }
  }

  checkIfModuleIsESM(id: number): string {
    if (this.metrics.interventionStructure !== undefined) {
      if (this.metrics.interventionStructure[id] !== undefined) {
        const moduleType: string = this.metrics.interventionStructure[id].type.toString();
        if (moduleType === this.MODULE_TYPE.ESM) {
          return ' (esm)';
        } else {
          return '';
        }
      } else {
        return '';
      }
    } else {
      return '';
    }
  }

  getIterationNumbers(module: Module, participantId: number): ParticipantIterationPivotId[] {
    const participantModuleIterations = [];
    this.getIterationTiming(module, participantId);
    if (participantId) {
      const moduleIds: Array<any> = this.metrics.partModIds[participantId];
      if (moduleIds[module.id] !== undefined) {
        if (moduleIds[module.id].length > 0) {
          const participantInfo = this.metrics.participants.find((participant) => participant.id === participantId);
          const modulePivots = participantInfo.modulePivots.filter((modulePivot) => modulePivot.moduleId === module.id);
          for (let i = 0; i < moduleIds[module.id].length; i++) {
            modulePivots.forEach((modulePivot) => {
              if (modulePivot.esmTriggerRuleTimeId === moduleIds[module.id][i]) {
                const newItem: ParticipantIterationPivotId = {
                  participantId,
                  iteration: modulePivot.esmTriggerRuleTime.iteration,
                  iterationId: modulePivot.esmTriggerRuleTime.id,
                  modulePivotId: modulePivot.id,
                  dropDown: false,
                  position: i,
                };
                participantModuleIterations.push(newItem);
              }
            });

            if (this.esmSelectedIteration[`${participantId}_${module.id}`] === undefined) {
              this.esmSelectedIteration[`${participantId}_${module.id}`] = participantModuleIterations[0];
            }
          }
        }
      }
    }
    return participantModuleIterations;
  }

  getSelectedIterationKey(participantId: number, moduleId: number): string {
    return `${participantId}_${moduleId}`;
  }

  toggleDropdown(participantId: number, moduleId: number) {
    this.esmSelectedIteration[`${participantId}_${moduleId}`].dropDown = !this.esmSelectedIteration[
      `${participantId}_${moduleId}`
    ].dropDown;
  }

  selectIteration(iter: ParticipantIterationPivotId, moduleId: number) {
    this.esmSelectedIteration[`${iter.participantId}_${moduleId}`] = iter;
    this.esmSelectedIteration[`${iter.participantId}_${moduleId}`].dropDown = false;
  }

  getIterationTiming(module: Module, participantId: number) {
    let modulePivotTemp: any = null;
    this.metrics?.participants?.forEach((participant) => {
      if (participant.id === participantId) {
        participant.modulePivots.forEach((modulePivot) => {
          if (module.id === modulePivot.moduleId) {
            if (this.esmSelectedIteration[`${participant.id}_${module.id}`]) {
              const iteration = this.esmSelectedIteration[`${participant.id}_${module.id}`].iteration;
              if (this.esmIterationModuleId[participantId + '' + iteration]) {
                if (modulePivot.id === this.esmIterationModuleId[participantId + '' + iteration]) {
                  modulePivotTemp = modulePivot;
                }
              }
            }
          }
        });
      }
    });
    return modulePivotTemp;
  }

  getResponseValues(module: Module, moduleItem: ModuleItem) {
    const moduleItemResponses = [];
    let isParticipantScreening = false;

    this.metrics?.participants?.forEach((participant) => {
      if (!this.metrics.partModIds) {
        isParticipantScreening = true;
      }

      const moduleIds: Array<any> = isParticipantScreening
        ? this.modules.map((screeningModule) => screeningModule.id)
        : this.metrics.partModIds[participant?.id];

      if (isParticipantScreening) {
        if (moduleIds.indexOf(module.id) !== -1) {
          const responsesType = this.getTypeOfResponsesArray(moduleItem?.type, moduleItem?.subType);
          if (participant[responsesType]) {
            if (moduleItem?.subType === ModuleItemAvailableSubType.MULTIPLE_CHOICE) {
              const chosenOptions = participant[responsesType]
                .filter((res) => res.moduleItemId === moduleItem.id)
                ?.map((option) => option.response);
              moduleItemResponses.push({ response: chosenOptions, participantId: participant?.id });
            } else {
              const response = participant[responsesType].find((res) => res.moduleItemId === moduleItem.id);
              moduleItemResponses.push(response);
            }
          } else {
            moduleItemResponses.push('-');
          }
        } else {
          moduleItemResponses.push('-');
        }
      } else {
        if (moduleIds[module.id] !== undefined) {
          if (moduleIds[module.id].length === 0) {
            const responsesType = this.getTypeOfResponsesArray(moduleItem?.type, moduleItem?.subType);
            if (participant[responsesType]) {
              if (moduleItem?.subType === ModuleItemAvailableSubType.MULTIPLE_CHOICE) {
                const chosenOptions = participant[responsesType]
                  .filter((res) => res.moduleItemId === moduleItem.id)
                  ?.map((option) => option.response);
                moduleItemResponses.push({ response: chosenOptions, participantId: participant?.id });
              } else {
                const response = participant[responsesType].find((res) => res.moduleItemId === moduleItem.id);
                moduleItemResponses.push(response);
              }
            } else {
              moduleItemResponses.push('-');
            }
          } else if (moduleIds[module.id].length >= 1) {
            moduleIds[module.id].forEach((iterationId) => {
              if (this.esmSelectedIteration[`${participant.id}_${module.id}`].iterationId === iterationId) {
                const iteration = this.esmSelectedIteration[`${participant.id}_${module.id}`].iteration;
                const responsesType = this.getTypeOfResponsesArray(moduleItem?.type, moduleItem?.subType);
                if (participant[responsesType]) {
                  if (moduleItem?.subType === ModuleItemAvailableSubType.MULTIPLE_CHOICE) {
                    const chosenOptions = participant[responsesType]
                      .filter(
                        (res) =>
                          res.moduleItemId === moduleItem.id &&
                          res.iteration === iteration &&
                          res.participantModulesId ===
                            this.esmSelectedIteration[`${participant.id}_${module.id}`].modulePivotId
                      )
                      ?.map((option) => option.response);
                    const fullResponse = participant[responsesType].filter(
                      (res) =>
                        res.moduleItemId === moduleItem.id &&
                        res.iteration === iteration &&
                        res.participantModulesId ===
                          this.esmSelectedIteration[`${participant.id}_${module.id}`].modulePivotId
                    );
                    if (chosenOptions && fullResponse) {
                      moduleItemResponses.push({
                        response: chosenOptions,
                        participantId: participant?.id,
                        fullResponse: fullResponse[0],
                      });
                      this.esmIterationModuleId[participant?.id + '' + iteration] = fullResponse.participantModulesId;
                    } else {
                      moduleItemResponses.push('-');
                    }
                  } else {
                    const response = participant[responsesType].find(
                      (res) =>
                        res.moduleItemId === moduleItem.id &&
                        res.iteration === iteration &&
                        res.participantModulesId ===
                          this.esmSelectedIteration[`${participant.id}_${module.id}`].modulePivotId
                    );
                    if (response) {
                      moduleItemResponses.push(response);
                      this.esmIterationModuleId[participant?.id + '' + iteration] = response.participantModulesId;
                    } else {
                      moduleItemResponses.push('-');
                    }
                  }
                } else {
                  moduleItemResponses.push('-');
                }
              }
            });
          }
        } else {
          moduleItemResponses.push('-');
        }
      }
    });
    return moduleItemResponses;
  }

  getTypeOfResponsesArray(type: string, subType: string): string {
    let responsesType: string;
    if (
      subType === ModuleItemAvailableSubType.LIKERT ||
      subType === ModuleItemAvailableSubType.MULTIPLE_CHOICE ||
      subType === ModuleItemAvailableSubType.YES_NO ||
      subType === ModuleItemAvailableSubType.YES_NO_OTHER ||
      (type === ModuleItemAvailableType.TEXT && subType === ModuleItemAvailableSubType.DROPDOWN)
    ) {
      responsesType = RESPONSES_TYPE.INDEX;
    } else if (
      subType === ModuleItemAvailableSubType.QUADRANT ||
      subType === ModuleItemAvailableSubType.IMAGE ||
      subType === ModuleItemAvailableSubType.AUDIO_RECORDING
    ) {
      responsesType = RESPONSES_TYPE.TEXT;
    } else if (subType === ModuleItemAvailableSubType.ORBITS) {
      responsesType = RESPONSES_TYPE.ORBIT;
    } else if (subType === ModuleItemAvailableSubType.NETWORK) {
      responsesType = RESPONSES_TYPE.NETWORK;
    } else if (subType === ModuleItemAvailableSubType.IVY) {
      responsesType = RESPONSES_TYPE.IVY;
    } else {
      responsesType = type;
    }
    return `${responsesType}Responses`;
  }

  getResponseType(subType: string): string {
    let answerType = ANSWERS_TYPE.TEXT;
    if (
      subType === ModuleItemAvailableSubType.MULTIPLE_CHOICE ||
      subType === ModuleItemAvailableSubType.YES_NO ||
      subType === ModuleItemAvailableSubType.YES_NO_OTHER
    ) {
      answerType = ANSWERS_TYPE.ICON;
    } else if (subType === ModuleItemAvailableSubType.IMAGE) {
      answerType = ANSWERS_TYPE.LINKS;
    } else if (subType === ModuleItemAvailableSubType.ORBITS || subType === ModuleItemAvailableSubType.NETWORK) {
      answerType = `${ANSWERS_TYPE.LINKS}-${ANSWERS_TYPE.ITEMS}`;
    }
    return answerType;
  }

  getResponseTiming(response, moduleItemId: number): string {
    let responseTiming;
    if (response?.participantId) {
      if (response?.iteration) {
        responseTiming = this.getParticipantByIdFromMetrics(response.participantId)?.responseTimings?.find(
          (timing) =>
            timing.moduleItemId === moduleItemId &&
            timing.iteration === response?.iteration &&
            timing.participantModulesId === response.participantModulesId
        );
      } else if (response?.fullResponse) {
        responseTiming = this.getParticipantByIdFromMetrics(response.participantId)?.responseTimings?.find(
          (timing) =>
            timing.moduleItemId === moduleItemId &&
            timing.iteration === response?.fullResponse?.iteration &&
            timing.participantModulesId === response?.fullResponse?.participantModulesId
        );
      } else {
        responseTiming = this.getParticipantByIdFromMetrics(response.participantId)?.responseTimings?.find(
          (timing) => timing.moduleItemId === moduleItemId
        );
      }
    }

    if (response?.deviceId) {
      responseTiming = this.getParticipantByDeviceIdFromMetrics(response.deviceId)?.responseTimings?.find(
        (timing) => timing.moduleItemId === moduleItemId
      );
    }

    return responseTiming?.milliseconds ? `${Math.round(responseTiming?.milliseconds / 100) / 10}s` : '';
  }

  showImageResponse(response: string, subType: string): void {
    if (!response || response === '-' || !response.startsWith('data')) {
      return;
    }
    const imageSrc = subType === ModuleItemAvailableSubType.IMAGE ? response : JSON.parse(response)?.image_data;
    this.dialog.open(MetricsDialogComponent, { data: { imageSrc } });
  }

  getImageData(response: string) {
    if (!response || response === '-' || !response.startsWith('{') || !response.endsWith('}')) {
      return;
    }
    return JSON.parse(response).image_data;
  }

  getOrbitItems(response: string): OrbitItem[] {
    if (!response || response === '-' || !response.startsWith('{') || !response.endsWith('}')) {
      return;
    }
    return JSON.parse(response)?.orbits;
  }

  getChoices(moduleItem: ModuleItem): string[] {
    const choicesAttribute = moduleItem?.attributes?.find(
      (attribute) => attribute.attribute === ModuleItemAvailableAttribute.CHOICES
    );
    return choicesAttribute?.translations[0]?.translation.split(/\n/).concat('other');
  }

  getHealths(module: Module): HealthData[] {
    const moduleHealths = [];

    if (this.participantIndex != null) {
      const participant = this.metrics?.participants[this.participantIndex];
      const moduleIds: Array<any> = this.metrics.partModIds[participant?.id];
      if (moduleIds[module.id] !== undefined) {
        if (moduleIds[module.id].length === 0) {
          participant.modulePivots.forEach((modulePivot) => {
            if (modulePivot.moduleId === module.id && modulePivot.participantId === participant.id) {
              if (participant.healthResponses.length > 0) {
                const health = participant.healthResponses.find(
                  (healthResponse) => healthResponse?.participantModulesId === modulePivot?.id
                );
                if (health) {
                  if (health.healthData !== undefined) {
                    moduleHealths.push(JSON.parse(health.healthData));
                  } else {
                    moduleHealths.push('');
                  }
                } else {
                  moduleHealths.push('');
                }
              } else {
                moduleHealths.push('');
              }
            }
          });
        }
      } else {
        moduleHealths.push('');
      }
    } else {
      this.metrics?.participants?.forEach((participant) => {
        const moduleIds: Array<any> = this.metrics.partModIds[participant?.id];
        if (moduleIds[module.id] !== undefined) {
          if (moduleIds[module.id].length === 0) {
            participant.modulePivots.forEach((modulePivot) => {
              if (modulePivot.moduleId === module.id && modulePivot.participantId === participant.id) {
                if (participant.healthResponses.length > 0) {
                  const health = participant.healthResponses.find(
                    (healthResponse) => healthResponse?.participantModulesId === modulePivot?.id
                  );
                  if (health) {
                    if (health.healthData !== undefined) {
                      moduleHealths.push(JSON.parse(health.healthData));
                    } else {
                      moduleHealths.push('');
                    }
                  } else {
                    moduleHealths.push('');
                  }
                } else {
                  moduleHealths.push('');
                }
              }
            });
          }
        } else {
          moduleHealths.push('');
        }
      });
    }
    return moduleHealths;
  }

  getPhenotypes(module: Module): PhenotypingData[] {
    const modulePhenotypes = [];

    if (this.participantIndex != null) {
      const participant = this.metrics?.participants[this.participantIndex];
      const moduleIds: Array<any> = this.metrics.partModIds[participant?.id];
      if (moduleIds[module.id] !== undefined) {
        if (moduleIds[module.id].length === 0) {
          participant.modulePivots.forEach((modulePivot) => {
            if (modulePivot.moduleId === module.id && modulePivot.participantId === participant.id) {
              if (participant.phenotypingResponses.length > 0) {
                const phenotype = participant.phenotypingResponses.find(
                  (phenotypeResponse) => phenotypeResponse?.participantModulesId === modulePivot?.id
                );
                if (phenotype) {
                  if (phenotype.phenotypingData !== undefined) {
                    const data = JSON.parse(phenotype.phenotypingData);
                    if (!Array.isArray(data.battery)) {
                      data.battery = [{ value: data.battery }];
                    }
                    if (!Array.isArray(data.location)) {
                      data.location = [{ value: data.location }];
                    }
                    modulePhenotypes.push(data);
                  } else {
                    modulePhenotypes.push('');
                  }
                } else {
                  modulePhenotypes.push('');
                }
              } else {
                modulePhenotypes.push('');
              }
            }
          });
        }
      } else {
        modulePhenotypes.push('');
      }
    } else {
      this.metrics?.participants?.forEach((participant) => {
        const moduleIds: Array<any> = this.metrics.partModIds[participant?.id];
        if (moduleIds[module.id] !== undefined) {
          if (moduleIds[module.id].length === 0) {
            participant.modulePivots.forEach((modulePivot) => {
              if (modulePivot.moduleId === module.id && modulePivot.participantId === participant.id) {
                if (participant.phenotypingResponses.length > 0) {
                  const phenotype = participant.phenotypingResponses.find(
                    (phenotypeResponse) => phenotypeResponse?.participantModulesId === modulePivot?.id
                  );
                  if (phenotype) {
                    if (phenotype.phenotypingData !== undefined) {
                      const data = JSON.parse(phenotype.phenotypingData);
                      if (!Array.isArray(data.battery)) {
                        data.battery = [{ value: data.battery }];
                      }
                      if (!Array.isArray(data.location)) {
                        data.location = [{ value: data.location }];
                      }
                      modulePhenotypes.push(data);
                    } else {
                      modulePhenotypes.push('');
                    }
                  } else {
                    modulePhenotypes.push('');
                  }
                } else {
                  modulePhenotypes.push('');
                }
              }
            });
          }
        } else {
          modulePhenotypes.push('');
        }
      });
    }
    return modulePhenotypes;
  }

  getHealthValue(dataType: string, health): HealthValue {
    const healthValue: HealthValue = { value: null, unit: '', date: null, startDate: null, endDate: null };
    if (!health) {
      return healthValue;
    }

    if (dataType === HEALTH_DATA_VALUES.STEPS) {
      healthValue.unit = 'count';
      healthValue.value = Math.round(health.value).toString();
      healthValue.startDate = new Date(health.startDate);
      healthValue.endDate = new Date(health.endDate);
    } else if (dataType === HEALTH_DATA_VALUES.HEART_RATE) {
      healthValue.unit = health ? 'bpm' : '';
      healthValue.value = health ? Math.round(health.value) : '';
      healthValue.date = new Date(health.date);
    } else if (dataType === HEALTH_DATA_VALUES.SLEEP) {
      healthValue.value = this.getSleepValue(health.value);
      healthValue.startDate = new Date(health.startDate);
      healthValue.endDate = new Date(health.endDate);
    }
    return healthValue;
  }

  getSleepValue(value: string): string {
    let sleepValue;
    switch (value) {
      case 'sleep.awake':
        sleepValue = HEALTH_DATA_SLEEP_VALUES.AWAKE;
        break;
      case 'sleep.asleep':
        sleepValue = HEALTH_DATA_SLEEP_VALUES.ASLEEP;
        break;
      case 'sleep.outOfBed':
        sleepValue = HEALTH_DATA_SLEEP_VALUES.OUT_OF_BED;
        break;
      case 'sleep.inBed':
        sleepValue = HEALTH_DATA_SLEEP_VALUES.IN_BED;
        break;
      case 'sleep.light':
        sleepValue = HEALTH_DATA_SLEEP_VALUES.LIGHT;
        break;
      case 'sleep.deep':
        sleepValue = HEALTH_DATA_SLEEP_VALUES.DEEP;
        break;
      case 'sleep.rem':
        sleepValue = HEALTH_DATA_SLEEP_VALUES.REM;
        break;
      default:
        sleepValue = value.toString();
        break;
    }
    return sleepValue;
  }

  getPhenotypingValue(dataType: string, phenotyping): PhenotypingValue {
    const phenotypingValue: PhenotypingValue = { value: null, unit: '' };
    if (!phenotyping) {
      return phenotypingValue;
    }

    if (dataType === PHENOTYPING_DATA_VALUES.BATTERY) {
      phenotypingValue.unit = phenotyping ? 'percent' : '';
      phenotypingValue.value = Math.round(Number(phenotyping.value.replace('%', '')));
    } else if (dataType === PHENOTYPING_DATA_VALUES.LOCATION) {
      phenotypingValue.value = phenotyping ? phenotyping.value : '';
    }
    return phenotypingValue;
  }

  setSelectedModuleItem(moduleItem: ModuleItem): void {
    this.selectedModuleItem = moduleItem;
  }

  getResponseValueByType(participantId: string | number, moduleItem: ModuleItem, typeOfResponse: string): string {
    if (participantId) {
      typeOfResponse = `${typeOfResponse}Responses`;
      const participant = this.getParticipantByIdFromMetrics(Number(participantId));
      return participant[typeOfResponse].find((response) => response.moduleItemId === moduleItem?.id)?.response;
    } else {
      return null;
    }
  }

  showHealthGraph(healthData: HealthData, healthDataType): void {
    const graphData = {
      healthData,
      healthDataType,
    };
    this.dialog.open(MetricsDialogComponent, { data: { graphData } });
  }

  healthActivitiesToArray(healthActivities): HealthActivity[] {
    if (healthActivities) {
      return Array.of(healthActivities);
    } else {
      return [];
    }
  }

  getIvyMetrics(response): string | undefined {
    try {
      return JSON.parse(response)?.wrong_answers_count;
    } catch (error) {
      return response;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
